import { useEffect, useRef } from 'react'

import { IconFlask } from '@tabler/icons-react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'

import {
  ResponsiveDialog,
  ResponsiveDialogBody,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogDismiss,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from '@/components/common/responsive-dialog'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import { redirectBeforeContextSwitch } from '@/lib/utils'
import { useContextStore } from '@/stores/context'
import { useSandboxStore } from '@/stores/sandbox'
import type { SandboxSource } from '@/stores/sandbox-dialog'

import locales from './locales'
import {
  trackSandboxDialogClosed,
  trackSandboxDialogOpened,
} from './sandbox-dialog.tracking'

interface SandboxDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  source: SandboxSource | null
}

export function SandboxDialog({
  open,
  onOpenChange,
  source,
}: SandboxDialogProps) {
  const { t } = useComponentTranslation(locales)
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const setIsSandboxMode = useSandboxStore((s) => s.setIsSandboxMode)

  const userRole = useContextStore((s) =>
    s.userAccountContext?.userAccountInformation.role === 'Administrator'
      ? 'Administrator'
      : 'User',
  )

  const currentPlan = useContextStore(
    (s) => s.userAccountContext?.balanceInformation.featureType ?? 'Free',
  )

  const isManaged = useContextStore(
    (s) => s.userAccountContext?.accountInformation.isManaged ?? false,
  )

  const openedAtRef = useRef<number | null>(null)
  const hasConverted = useRef(false)
  const hasTrackedOpen = useRef(false)

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      trackSandboxDialogOpened({
        source,
        userRole,
        currentPlan,
        isManaged,
      })
      hasTrackedOpen.current = true
      openedAtRef.current = Date.now()
    }

    if (!open) {
      hasTrackedOpen.current = false
    }
  }, [open, source, userRole, currentPlan, isManaged])

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && open) {
      const durationMs = openedAtRef.current
        ? Date.now() - openedAtRef.current
        : 0

      trackSandboxDialogClosed({
        source,
        userRole,
        currentPlan,
        isManaged,
        converted: hasConverted.current,
        durationMs,
      })

      hasConverted.current = false
      openedAtRef.current = null
    }
    onOpenChange(nextOpen)
  }

  const disableSandbox = async () => {
    hasConverted.current = true
    handleOpenChange(false)
    // Leave any record-scoped page (and clear an unsaved-changes prompt)
    // BEFORE switching scope. If the user cancels the prompt, abort.
    const canProceed = await redirectBeforeContextSwitch((to) =>
      navigate({ to }),
    )
    if (!canProceed) return
    setIsSandboxMode(false)
    queryClient.clear()
    window.location.reload()
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent>
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle
            icon={<IconFlask className="text-orange-600" />}
          >
            {t('title')}
          </ResponsiveDialogTitle>
          <ResponsiveDialogDescription>
            <strong>{t('currentlyInTestMode')}</strong> {t('thisMeans')}
          </ResponsiveDialogDescription>
        </ResponsiveDialogHeader>
        <ResponsiveDialogBody>
          <ul className="space-y-2 pl-4 text-sm">
            <li className="flex gap-2">
              <span className="text-orange-600">•</span>
              <span>
                {t('point1')} <strong>{t('point1Bold')}</strong>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-orange-600">•</span>
              <span>
                {t('point2')} <strong>{t('point2Bold')}</strong>
              </span>
            </li>
            <li className="flex gap-2">
              <span className="text-orange-600">•</span>
              <span>{t('point3')}</span>
            </li>
          </ul>
        </ResponsiveDialogBody>
        <ResponsiveDialogFooter>
          <ResponsiveDialogDismiss intent="close" />
          <Button
            onClick={disableSandbox}
            className="gap-2 bg-orange-600 text-white hover:bg-orange-700 focus-visible:border-orange-600 focus-visible:ring-orange-600/50 dark:bg-orange-700 dark:hover:bg-orange-800 dark:focus-visible:border-orange-700 dark:focus-visible:ring-orange-700/50"
          >
            {t('disableTestMode')}
          </Button>
        </ResponsiveDialogFooter>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
